import React, { useCallback, useState } from 'react'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../../../common/integrations'
import { type CrmIntegrationSettings } from '../../../Integration/IntegrationManagement/OnboardingSupport/types'
import { PostConnectStepFrame } from '../PostConnectStepFrame'
import { OnboardingStepHeader } from '../../Onboarding/OnboardingStepHeader/OnboardingStepHeader'
import { ToggleCard } from '../Components/ToggleCard'
import { getOnboardingCrmSettingsProvider } from '../../../Integration/IntegrationManagement/OnboardingSupport/settingsProviders'
import { POST_CONNECT_COPIES } from '../../copies/onboardingCopies'

export interface CrmCalendarSyncStepProps {
  serviceId: IntegrationServiceId
  useConnectedHeader: boolean
  onContinue: () => void
  onExit: () => void
  onBack?: () => void
}

interface CrmCalendarSyncStepViewProps extends CrmCalendarSyncStepProps {
  settings: CrmIntegrationSettings
}

const CrmCalendarSyncStepView: React.FC<CrmCalendarSyncStepViewProps> = ({ serviceId, useConnectedHeader, settings, onContinue, onExit, onBack }) => {
  const { isFetching, save } = settings
  const displayName = getIntegrationDisplayName(serviceId)

  const [localCalendarSync, setLocalCalendarSync] = useState(true)

  const handleSave = useCallback(async () => {
    await save({ calendarSync: localCalendarSync })
  }, [save, localCalendarSync])

  return (
    <PostConnectStepFrame
      loading={isFetching}
      onSave={handleSave}
      onAdvance={localCalendarSync ? onContinue : onExit}
      onBack={onBack}
      nextLabel={localCalendarSync ? 'Next' : 'Done'}
    >
      <OnboardingStepHeader
        icon={useConnectedHeader ? <i className='bi bi-check-circle text-success' /> : <i className='bi bi-sliders2' />}
        title={useConnectedHeader ? `${displayName} connected` : `Sync meetings from ${displayName}`}
        subtitle={POST_CONNECT_COPIES['crm-calendar-sync'].headerSubtitle}
      />

      <p className='m-0 fw-bold fs-5 text-center text-dark'>Sync your meetings</p>

      <ToggleCard
        title={`Sync calendar from ${displayName}`}
        description={
          <>
            <strong>Individual</strong> – not recurring – meetings will be synced
          </>
        }
        checked={localCalendarSync}
        onChange={setLocalCalendarSync}
      />
    </PostConnectStepFrame>
  )
}

export const CrmCalendarSyncStep: React.FC<CrmCalendarSyncStepProps> = ({ serviceId, useConnectedHeader, onContinue, onExit, onBack }) => {
  const Provider = getOnboardingCrmSettingsProvider(serviceId)
  if (!Provider) {
    return null
  }

  return (
    <Provider>
      {(settings: CrmIntegrationSettings) => (
        <CrmCalendarSyncStepView
          serviceId={serviceId}
          useConnectedHeader={useConnectedHeader}
          settings={settings}
          onContinue={onContinue}
          onExit={onExit}
          onBack={onBack}
        />
      )}
    </Provider>
  )
}
